import { useCallback, useMemo } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  ConversationDocument,
  MatterConversationsDocument,
  MessagesDocument,
} from '~/graphql/queries/conversation';
import {
  SendMessageDocument,
  MarkConversationReadDocument,
} from '~/graphql/mutations/conversationMutations';
import { DEFAULT_MESSAGE_LIMIT } from '~/shared/constants';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';

interface Props {
  matterId: string;
  expertId: string;
  skip?: boolean;
}

export const useMessages = ({ matterId, expertId, skip = false }: Props) => {
  const apolloClient = useApolloClient();
  const { user: currentUser } = useCurrentUser();

  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
  } = useQuery(ConversationDocument, {
    variables: { matterId, expertId },
    skip,
  });

  const conversation = conversationData?.conversation;
  const conversationId = conversation?.id;

  const {
    data: messagesData,
    loading: messagesLoading,
    error: messagesError,
    fetchMore,
  } = useQuery(MessagesDocument, {
    variables: {
      conversationId: conversationId!,
      limit: DEFAULT_MESSAGE_LIMIT,
    },
    skip: !conversationId,
  });

  const messages = useMemo(
    () => messagesData?.messages ?? [],
    [messagesData?.messages],
  );

  const [sendMessageMutation, { loading: isSending }] =
    useMutation(SendMessageDocument);

  const [markReadMutation] = useMutation(MarkConversationReadDocument);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!conversationId) {
        return;
      }

      await sendMessageMutation({
        variables: { conversationId, content },
        optimisticResponse: currentUser
          ? {
              sendMessage: {
                __typename: 'Message',
                id: `optimistic-${Date.now()}`,
                conversationId,
                senderId: currentUser.id,
                content,
                createdAt: new Date().toISOString(),
                sender: {
                  __typename: 'AuthProfile',
                  id: currentUser.id,
                  email: currentUser.email,
                },
              },
            }
          : undefined,
        update(cache, { data }) {
          if (!data?.sendMessage) {
            return;
          }

          cache.updateQuery(
            {
              query: MessagesDocument,
              variables: {
                conversationId,
                limit: DEFAULT_MESSAGE_LIMIT,
              },
            },
            (existing) => {
              if (!existing) {
                return { messages: [data.sendMessage] };
              }

              const withoutOptimistic = existing.messages.filter(
                (message) => !String(message.id).startsWith('optimistic-'),
              );

              const alreadyExists = withoutOptimistic.some(
                (message) => message.id === data.sendMessage.id,
              );
              if (alreadyExists) {
                return { ...existing, messages: withoutOptimistic };
              }

              return {
                ...existing,
                messages: [data.sendMessage, ...withoutOptimistic],
              };
            },
          );
        },
      });
    },
    [conversationId, sendMessageMutation, currentUser],
  );

  const markConversationRead = useCallback(async () => {
    if (!conversationId) {
      return;
    }

    await markReadMutation({
      variables: { conversationId },
      refetchQueries: [
        { query: MatterConversationsDocument, variables: { matterId } },
      ],
    });
  }, [conversationId, matterId, markReadMutation]);

  const fetchOlderMessages = useCallback(async () => {
    if (!conversationId || messages.length === 0) {
      return;
    }

    const oldestMessage = messages[messages.length - 1];

    const { data } = await fetchMore({
      variables: {
        conversationId,
        limit: DEFAULT_MESSAGE_LIMIT,
        before: oldestMessage.id,
      },
      updateQuery(existing, { fetchMoreResult }) {
        if (!fetchMoreResult?.messages.length) {
          return existing;
        }

        return {
          ...existing,
          messages: [...existing.messages, ...fetchMoreResult.messages],
        };
      },
    });

    return data.messages.length > 0;
  }, [conversationId, messages, fetchMore]);

  const fetchAllMessages = useCallback(async () => {
    if (!conversationId) {
      return [];
    }

    const allMessages: typeof messages = [];
    let cursor: string | undefined;

    for (;;) {
      const { data } = await apolloClient.query({
        query: MessagesDocument,
        variables: {
          conversationId,
          limit: DEFAULT_MESSAGE_LIMIT,
          ...(cursor ? { before: cursor } : {}),
        },
        fetchPolicy: 'network-only',
      });

      if (!data.messages.length) {
        break;
      }

      allMessages.push(...data.messages);
      cursor = data.messages[data.messages.length - 1].id;

      if (data.messages.length < DEFAULT_MESSAGE_LIMIT) {
        break;
      }
    }

    return allMessages;
  }, [conversationId, apolloClient]);

  return {
    conversation: conversation ?? null,
    messages,
    isLoading: conversationLoading || messagesLoading,
    isSending,
    error: conversationError ?? messagesError ?? null,
    sendMessage,
    markConversationRead,
    fetchOlderMessages,
    fetchAllMessages,
  };
};
